import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IFile } from '@shared/interfaces/file';
import { FilesApiService } from '@bcmApiServices/files.api-service';
import { isString } from '../../functions/is-string';

@Component({
    selector: 'u2b-secured-image-bcm',
    styles: [`
        img {
            width: 100%;
        }
        .image-loader {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
        }
    `],
    template: `
        <ng-container *ngIf="dataUrl; else imageLoader">
            <img class="{{class}}"
                 alt=""
                 [class.is-placeholder]="isPlaceholder"
                 [class.is-real-image]="!isPlaceholder"
                 [src]="dataUrl"
                 (error)="onImageError()"/>
        </ng-container>
        <ng-template #imageLoader>
            <div class="image-loader">
                <mat-spinner mode="indeterminate" [diameter]="20"></mat-spinner>
            </div>
        </ng-template>
    `,
    standalone: false
})
export class SecuredImageBcmComponent implements OnChanges, OnDestroy {

    @Input() public class: string;

    @Input() public file: IFile;

    @Input() public fileId: number;

    @Input() public tempUrl: SafeUrl;

    @Input() public placeholder = 'assets/images/placeholder/default.jpg';

    private src$: BehaviorSubject<[IFile, number]>;

    private createdObjectUrl: string | null = null;

    private imageLoadFailed = false;

    public dataUrl: SafeUrl;

    public isPlaceholder = true;

    constructor(private _filesApiService: FilesApiService,
                private domSanitizer: DomSanitizer,
                private cdr: ChangeDetectorRef) {
        this.src$ = new BehaviorSubject([this.file, this.fileId]);
        this.src$
            .pipe(switchMap((file: [IFile, number]) => this.loadImage(file)))
            .subscribe((url: SafeUrl) => {
                this.dataUrl = url;
                this.cdr.markForCheck();
            });
    }

    ngOnChanges(): void {
        this.imageLoadFailed = false;

        if (this.tempUrl) {
            this.src$.next([null, null] as [IFile, number]);
            return;
        }
        this.src$.next([this.file, this.fileId]);
    }

    ngOnDestroy(): void {
        this.revokeCreatedObjectUrl();
        this.src$.complete();
    }

    onImageError(): void {
        if (this.isPlaceholder || this.imageLoadFailed) {
            return;
        }

        this.imageLoadFailed = true;
        this.revokeCreatedObjectUrl();
        this.isPlaceholder = true;
        this.dataUrl = this.tempUrl || this.placeholder;
        this.cdr.markForCheck();
    }

    private loadImage([file, fileId]: [IFile, number]): Observable<any> {

        if (!file?.id && !fileId) {
            this.isPlaceholder = true;
            this.revokeCreatedObjectUrl();
            return of(this.tempUrl || this.placeholder);
        }

        return this._filesApiService.getById(file?.id || fileId)
            .pipe(
                map((e: Blob | string) => {
                    this.isPlaceholder = false;
                    this.imageLoadFailed = false;

                    if (!(e as Blob)?.size) {
                        throw new Error();
                    }

                    if (isString(e)) {
                        this.revokeCreatedObjectUrl();
                        return this.domSanitizer.bypassSecurityTrustUrl(e as string);
                    }

                    this.revokeCreatedObjectUrl();
                    this.createdObjectUrl = URL.createObjectURL(e as Blob);
                    return this.domSanitizer.bypassSecurityTrustUrl(this.createdObjectUrl);
                }),
                catchError(() => {
                    this.isPlaceholder = true;
                    this.revokeCreatedObjectUrl();
                    return of(this.tempUrl || this.placeholder);
                }),
            );
    }

    private revokeCreatedObjectUrl(): void {
        if (this.createdObjectUrl) {
            URL.revokeObjectURL(this.createdObjectUrl);
            this.createdObjectUrl = null;
        }
    }
}
